import React, {useEffect, useState} from 'react';
import {ToastContainer} from 'react-toastify';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {
    AlertIcon,
    CrossLight,
    Diamond,
    ShareIcon,
    UserAvatar,
    UserCheckAvatar,
    UserFirstPosition,
} from 'src/ui-components/Iconpack';
import {
    Tooltip,
    TooltipArrow,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from 'src/ui-components/Tooltip/tooltip';
import UserReferral from 'src/components/user-referral/UserReferral';
import FriendRefer from 'src/assets/icons/friend_refer.svg';
import {Dialog, DialogContent, DialogTrigger} from 'src/ui-components/Modal';
import {useSelector} from 'react-redux';
import {RootState, useAppDispatch} from 'src/store';
import Tracker from 'src/lib/analytics/tracker';
import {EventNames, EventProperty} from 'src/lib/analytics/events';
import {getMetrics, TimeSpanType} from '../../actions/metrics';
import DollarCoin from '../../assets/animations/dollar-coin.gif';
import {Button} from '../../ui-components/Button/button';
import AIImage from '../../assets/home/AIImage.svg';
import Layouts from '../../assets/home/Layouts.svg';
import Shapes from '../../assets/home/Shapes.svg';
import Templates from '../../assets/home/Templates.svg';
import Texts from '../../assets/home/Texts.svg';
import Tshirt from '../../assets/home/Tshirt.svg';
import Ellipse from '../../assets/home/Ellipse.svg';
import {getMediaFiles} from '../../actions/media';
import {MediaFile} from '../../reducers/media';
import {getDayCounter, getDaySinceJoined} from '../../module/pixel-design-studio/utils/helper';

const DashboardNew: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const source = searchParams.get('source');
    const KB_100 = 100 * 1024;
    const ALERT_THRESHOLD = 10; // alert if available credit is <= 10
    const dispatch = useAppDispatch();
    const [lowCredit, setLowCredit] = useState<string | undefined>();
    const [remainingCreditsPercent, setRemainingCreditsPercent] = useState<number>(100);
    const [isNewUser, setIsNewUser] = useState(true);
    const [loadingMetrics, setLoadingMetrics] = useState(true);
    const [walletBalance, setWalletBalance] = useState(0);
    const [showWalletDialog, setShowWalletDialog] = useState(false);
    const [tableData, setTableData] = useState<MediaFile[]>();
    const [dayOfTrial, setDayOfTrial] = useState(0);
    const [tier, setTier] = useState('');

    const [mediaProps, userProfileProps, metricsProps] = [
        useSelector((state: RootState) => state.media),
        useSelector((state: RootState) => state.userProfile),
        useSelector((state: RootState) => state.metrics),
    ];
    const navigate = useNavigate();

    const {referral_code, referral_count} = userProfileProps;
    const onFileAdd = (files: FileList) => {
        Tracker.trackEvent(EventNames.FILE_UPLOAD_FROM_HOMEPAGE);
        navigate('/media-library', {state: {files}});
    };

    const onViewPlans = () => {
        Tracker.trackEvent(EventNames.VIEW_PLANS_ON_ALERT);
        navigate('/settings/plans');
    };

    const openPixelClassic = () => {
        if (tableData && tableData.length) {
            navigate(`transformation/${tableData[0]['file_uid']}`);
        } else {
            navigate('/trial');
        }
    };

    useEffect(() => {
        if (userProfileProps.date_joined) {
            let tier = '';
            if (userProfileProps.currentOrganization && userProfileProps.currentOrganization.tier) {
                tier = userProfileProps.currentOrganization.tier.name;
            }
            Tracker.trackEvent(EventNames.DAYS_SINCE_JOINED, {
                [EventProperty.DaysOnPixel]: getDaySinceJoined(userProfileProps.date_joined),
                [EventProperty.Tier]: tier,
            });
            setDayOfTrial(getDayCounter(userProfileProps.date_joined));
            if (userProfileProps.org_admins.length && userProfileProps.org_admins[0].tier) {
                setTier(userProfileProps.org_admins[0].tier.name);
            }
        }
    }, [userProfileProps.date_joined]);

    useEffect(() => {
        const {error} = metricsProps;
        if (error || !metricsProps.isDefault) {
            setLoadingMetrics(false);
        }
    }, [metricsProps.error, metricsProps.limits, metricsProps.usages, metricsProps]);

    useEffect(() => {
        if (metricsProps.isLoading || metricsProps.isDefault) return;

        const {usages, limits} = metricsProps;

        const isNewUser = !(usages.storage > KB_100 || usages.bandwidth > KB_100);
        setIsNewUser(isNewUser);

        const calculateAvailabilityPercentage = (used: number, limit: number) =>
            limit > 0 ? ((limit - used) / limit) * 100 : 0;

        const availableStorage = calculateAvailabilityPercentage(usages.storage, limits.storage);
        const availableBw = calculateAvailabilityPercentage(usages.bandwidth, limits.bandwidth);

        if (availableStorage <= ALERT_THRESHOLD) {
            setLowCredit('storage');
            setRemainingCreditsPercent(availableStorage);
        } else if (availableBw <= ALERT_THRESHOLD) {
            setLowCredit('bandwidth');
            setRemainingCreditsPercent(availableBw);
        } else {
            setLowCredit(undefined);
        }
    }, [metricsProps]);

    useEffect(() => {
        if (metricsProps.balance) {
            setWalletBalance(metricsProps.balance);
        }
    }, [metricsProps.balance]);

    useEffect(() => {
        if (mediaProps.files) {
            setTableData(mediaProps.files);
        }
    }, [mediaProps.files]);

    useEffect(() => {
        if (userProfileProps.currentOrganization.uid.length > 0) {
            if (!mediaProps.files.length) {
                dispatch(
                    getMediaFiles({orgUid: userProfileProps.currentOrganization.uid, folder: '', limit: 25, offset: 0}),
                );
            }
        }
    }, [userProfileProps.currentOrganization]);

    useEffect(() => {
        if (userProfileProps.org_admins.length !== 0 && metricsProps.limits.storage <= 0 && !metricsProps.isLoading) {
            const org_uid = userProfileProps.org_admins[0].uid;
            if (userProfileProps.org_admins[0].tier) {
                setTier(userProfileProps.org_admins[0].tier.name);
            }
            dispatch(getMetrics({org_uid, timespan_type: TimeSpanType.LastOneMonth}));
        }
    }, [userProfileProps.org_admins]);

    useEffect(() => {
        if (walletBalance && walletBalance > 0 && source === 'welcome') {
            setShowWalletDialog(true);
        }
    }, [walletBalance]);

    return (
        <section className='my-[10px] space-y-2 px-[1rem] md:my-[35px] md:space-y-3 md:pl-[34px] md:pr-[54px]'>
            {tableData && (
                <Dialog>
                    {/*upgrade nudge on low credit...*/}
                    {lowCredit && (
                        <div
                            className={
                                'flex h-20 w-full items-center justify-start gap-2 rounded-xl border-[1px] border-[#FEC84B] bg-[#FFFCF5] px-2 md:gap-5 md:px-6'
                            }>
                            <div>
                                <AlertIcon />
                            </div>
                            <div
                                className={`flex-col text-[11px] md:text-[14px] ${remainingCreditsPercent <= 0 ? 'text-[#FF4D4F]' : 'text-[#B54708]'}`}>
                                <span className={'font-[600]'}>
                                    {remainingCreditsPercent > 0
                                        ? `You are approaching the "${lowCredit}" limit for the current tier`
                                        : `You don't have enough "${lowCredit}" credits.`}
                                </span>
                                <div className={'flex items-stretch md:gap-2'}>
                                    <span className={'font-[400]'}>
                                        Consider upgrading your plan for best experience.
                                    </span>
                                    <div
                                        className={
                                            'flex cursor-pointer items-end whitespace-nowrap font-[400] text-[#155EEF]'
                                        }
                                        onClick={onViewPlans}>
                                        <span>View Plans</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {showWalletDialog && (
                        <div className='relative flex h-20 w-full items-center justify-start gap-2 rounded-xl border-[0.5px] border-[#828282] bg-[#D8E1FF] px-2 py-8 md:gap-5 md:px-6'>
                            <div
                                className='absolute right-2 top-2 cursor-pointer'
                                onClick={() => setShowWalletDialog(false)}>
                                <CrossLight />
                            </div>
                            <div className='flex w-full items-center'>
                                <img src={DollarCoin} alt='dollar' width={66} height={66} />
                                <div className='ml-6 flex flex-grow flex-col text-[11px] font-[500] md:text-[14px]'>
                                    <span>
                                        <span className='text-[16px] font-[700]'>Congratulations!</span> You have earned{' '}
                                        <span className='font-[800]'>{walletBalance}</span> credits for using the
                                        referral code.
                                    </span>
                                    <span>Use it to upgrade your account to a higher tier.</span>
                                </div>
                                <Button
                                    className='mr-[65px] h-[39px] w-[115px] bg-[#363F72]'
                                    onClick={() => navigate('/settings/plans')}>
                                    View Plan
                                </Button>
                            </div>
                        </div>
                    )}
                    <div className='relative mt-[30px] rounded-[12px] border border-[#EAECF0] px-[34px] py-[28px] md:mt-[0px]'>
                        <div className='absolute top-[-15px] flex items-center justify-center rounded-[5px] bg-gradient-to-r from-[#333333] to-[#545454] px-[10px] py-[5px] text-[12px] font-[900] text-white'>
                            New Launch
                        </div>
                        {tier === 'Starter' && dayOfTrial > 0 && (
                            <div className='flex w-[150px] items-center justify-between rounded-[6px] border border-[#EAECF0] bg-gradient-to-r from-[#A35FFC] to-[#FD5FC5] bg-clip-text px-[9px] py-[10px] text-[12px] font-[900] text-transparent md:hidden'>
                                <span>FREE TRIAL</span>
                                <span>{`${dayOfTrial} / 7 left`}</span>
                            </div>
                        )}

                        <div className='items-center justify-between md:flex'>
                            <span className='inline-block bg-gradient-to-r from-[#A35FFC] to-[#FD5FC5] bg-clip-text text-[46px] font-[900] text-transparent'>
                                PixelStudio
                            </span>
                            {tier === 'Starter' && dayOfTrial > 0 && (
                                <div className='hidden items-center justify-between gap-[7px] rounded-[6px] border border-[#EAECF0] bg-gradient-to-r from-[#A35FFC] to-[#FD5FC5] bg-clip-text px-[9px] py-[10px] text-[12px] font-[900] text-transparent md:flex'>
                                    <Diamond />
                                    <span>FREE TRIAL</span>
                                    <span>{`${dayOfTrial} / 7 left`}</span>
                                </div>
                            )}
                        </div>

                        <div className='mt-3'>
                            <span className='text-[16px] font-[400] leading-[24px] text-[#475467]'>
                                Unleash your creativity with PixelStudio's AI image generator, templates, and more.
                                Create your designs effortlessly.
                            </span>
                            <div className='mt-[30px] flex justify-between'>
                                <div className='hidden items-center gap-[28px] md:flex'>
                                    <img src={AIImage} alt='ai images' className='pt-[11px]' />
                                    <div className='relative'>
                                        <img src={Shapes} alt='shapes' />
                                        <img src={Ellipse} alt='Ellipse' className='absolute right-0 top-0' />
                                    </div>
                                    <img src={Texts} alt='texts' className='pt-[11px]' />
                                    <img src={Templates} alt='templates' className='pt-[11px]' />
                                    <img src={Tshirt} alt='tshirt' className='pt-[11px]' />
                                    <img src={Layouts} alt='layout' className='pt-[11px]' />
                                </div>

                                <div className='flex items-center justify-between'>
                                    <Button
                                        variant='primary'
                                        className='bg-gradient-to-r from-[#0017E4] to-[#3793FF] p-6 text-[18px] font-[700] text-white hover:bg-primary-hover'
                                        onClick={() => navigate('/PixelStudio')}>
                                        Explore PixelStudio
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='rounded-[12px] border border-[#EAECF0] px-[34px] py-[28px]'>
                        <span className='inline-block text-[46px] font-[900] text-[#000000]'>PixelClassic</span>
                        <div className='mt-3 items-center justify-between md:flex'>
                            <div>
                                <span className='text-[16px] font-[400] leading-[24px] text-[#475467]'>
                                    Simple and easy to use editor. Edit and upload images to CDN.
                                </span>
                            </div>
                            <div className='mt-4 flex items-center md:mt-0'>
                                <Button
                                    variant='secondary'
                                    className='border-[1.18px] border-[#262626] bg-white p-6 text-[18px] font-[700] text-[#101828]'
                                    onClick={openPixelClassic}>
                                    Open PixelClassic
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className='hidden items-center justify-between rounded-[0.5rem] border border-[#EAECF0] bg-[#FFFFFF] md:flex'>
                        <div className='flex flex-col p-[1.5rem]'>
                            <span
                                className={`w-[max-content] rounded-[0.25rem] 
                    bg-[#C7D7FE] px-[0.875rem] py-[0.313rem] text-[1.125rem] 
                    font-[900] text-[#2D3282]`}>
                                REFER & GET ADDITIONAL BANDWIDTH
                            </span>
                            <span className='pt-[1rem] text-[1rem] font-[400] text-[#000000]'>
                                Get <span className='font-[700]'>2 GB</span> on referring a friend. Add{' '}
                                <span className='font-[700]'>20 GB</span> of free bandwidth.
                            </span>
                            <div className='relative mt-[71px] pb-[1.25rem] pt-[43px]'>
                                <div className='h-[6px] w-[560px] bg-[#B3B8DB] pl-[5px]'></div>
                                <div className='absolute left-[-2px] top-[-10px] flex w-[565px] justify-between pt-[43px]'>
                                    {Array.from({length: 10}, (_, index) => (
                                        <div key={index} className='flex h-6 items-center'>
                                            {index < referral_count ? (
                                                <UserCheckAvatar />
                                            ) : index === referral_count ? (
                                                <TooltipProvider key={index}>
                                                    <Tooltip>
                                                        <TooltipTrigger>
                                                            <UserFirstPosition />
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>You are here</p>
                                                            <TooltipArrow />
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            ) : (
                                                <UserAvatar />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className='flex flex-col pr-[3.75rem] pt-[1.875rem]'>
                            <img src={FriendRefer} alt='refer friend' height={146} width={146} />
                            <DialogTrigger asChild>
                                <div
                                    className='flex cursor-pointer gap-[0.5rem] pt-[1rem]'
                                    onClick={() => Tracker.trackEvent(EventNames.SHARE_INVITE_CLICKED)}>
                                    <ShareIcon />
                                    <span className='text-[1rem] font-[700] text-[#155EEF]'>Share Invite</span>
                                </div>
                            </DialogTrigger>
                        </div>
                    </div>
                    <DialogContent className='w-[972px]'>
                        <UserReferral code={referral_code} referralCount={referral_count} />
                    </DialogContent>
                    <ToastContainer position={'bottom-right'} autoClose={4000} />
                </Dialog>
            )}
        </section>
    );
};

export default DashboardNew;
